import { cx } from '@linaria/core';
import { memo, useRef, useCallback, useEffect } from 'react';
import { throttleToAnimationFrame } from '@utils/common';
import { useSwipe } from '@utils/react';
import { useInfiniteScrolling, getTransformation } from './hook.infinite.js';
import { useRegisterKeyboardEvents } from './hook.keyboard.js';
import { SliderItem } from './item.js';
import { slider, sliderTokens, spacer, wrapper } from './slider.styles.js';
import { jsx, jsxs } from 'react/jsx-runtime';

/** Wrapper on top of memo to ensure that generic type can be propagated */
const genericMemo = memo;

/**
 * generic slider that can wraps an arbitrary amount of children in a carousel,
 * fully equipped with keyboard, touch and mouse-events.
 */
const Slider = genericMemo(function Slider(props) {
  const sliderRef = useRef(null);
  const {
    disabled,
    spacingBefore,
    items: inifiniteItems
  } = useInfiniteScrolling(sliderRef, props.pagination, props.items);
  useRoundedSliderWidth(props.wrapperRef, sliderRef);
  const onSwipe = useCallback(direction => {
    switch (direction) {
      case "right-to-left":
        props.pagination.goForwards();
        break;
      case "left-to-right":
        props.pagination.goBackwards();
        break;
    }
  }, [props.pagination]);
  useSwipe(props.wrapperRef, onSwipe, {
    touchOnly: true
  });
  const hasFocusWithin = useRegisterKeyboardEvents(props.pagination, sliderRef, {
    target: props.focusTarget ?? "left"
  });
  const items = props.infinite ? inifiniteItems : props.items.map((it, index) => ({
    item: {
      ...it,
      index,
      offsetIndex: index,
      key: String(index)
    },
    disabled: false
  }));
  return /*#__PURE__*/jsx("div", {
    ref: props.wrapperRef,
    className: cx(props.className, wrapper),
    children: /*#__PURE__*/jsxs("ul", {
      tabIndex: hasFocusWithin ? undefined : 0,
      ref: sliderRef,
      className: slider,
      style: {
        transform: !props.infinite || !disabled ? getTransformation(props.pagination, props.infinite) : undefined
      },
      children: [props.infinite && !disabled && /*#__PURE__*/jsx("div", {
        className: spacer,
        style: {
          width: `calc(${spacingBefore} * (100% + ${sliderTokens.itemMargin()}))`
        }
      }), items.map(it => /*#__PURE__*/jsx(SliderItem, {
        disabled: it.disabled,
        item: it.item,
        sliderRef: sliderRef,
        infinite: props.infinite,
        pagination: props.pagination,
        children: props.children
      }, it.item.key))]
    })
  });
});

/**
 * Annoyingly having a slider with a non-rounded decimal as its width may
 * quickly result in slight "jumps" when adjust the spacer sizing after
 * finishing a transition.
 *
 * We cannot change the default behavior of browsers and as such we round the
 * width of the slider to the nearest integer that makes sense. This avoids
 * jumps related to rounding errors.
 */
function useRoundedSliderWidth(wrapperRef, sliderRef) {
  useEffect(() => {
    const wrapper = wrapperRef.current;
    const slider = sliderRef.current;
    if (!wrapper || !slider) {
      return;
    }
    const onResize = () => {
      slider.style.width = `calc(${wrapper.clientWidth}px - 2 * ${sliderTokens.containerPadding()}`;
    };
    onResize();
    const observer = new ResizeObserver(throttleToAnimationFrame(onResize));
    observer.observe(wrapper);
    return () => {
      observer.disconnect();
    };
  }, [sliderRef, wrapperRef]);
}

export { Slider };
//# sourceMappingURL=slider.js.map
